import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../../software/api";
import { toast } from "sonner";
import { exportXLSX } from "../reports/exportUtils";
import { ClipboardList, X, Trash2, Eye, Printer, Mail, PackagePlus, RotateCcw, History } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

const inp = "border border-[#9db8d6] bg-white text-slate-900 px-1.5 py-0.5 text-[12px] rounded-sm outline-none focus:border-[#123a63] w-full";
const yellowInp = "border border-[#c9a800] bg-[#fff29a] text-slate-900 px-1.5 py-0.5 text-[12px] rounded-sm outline-none focus:border-[#123a63] w-full font-semibold text-right";
const cell = "px-1.5 py-1 border border-[#b9b9b9] bg-white text-[12px] whitespace-nowrap";
const blueBtn = "rounded-sm bg-[#1a4f8a] hover:bg-[#123a63] text-white text-[11px] font-black px-2 py-1 whitespace-nowrap";

// ---------- Weighted Average Cost / Manual correction modal ----------
function ItemCostModal({ row, prefillCost, onClose, onDone }) {
  const [mode, setMode] = useState("receive"); // receive | manual
  const [qty, setQty] = useState("1");
  const [unitCost, setUnitCost] = useState(prefillCost ? String(prefillCost) : String(row.cost_price || ""));
  const [manualCost, setManualCost] = useState(String(row.cost_price || ""));
  const [busy, setBusy] = useState(false);

  const oldQty = num(row.qty), oldCost = num(row.cost_price);
  const recvQty = num(qty), recvCost = num(unitCost);
  const projQty = oldQty + recvQty;
  const projCost = projQty > 0 ? ((Math.max(oldQty, 0) * oldCost) + (recvQty * recvCost)) / projQty : recvCost;

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "manual") {
        const r = await api("POST", `/products/${row.id}/stocktake`, { cost_price: num(manualCost) });
        toast.success("Item cost corrected.");
        onDone({ ...row, cost_price: r.cost_price });
      } else {
        if (recvQty <= 0) { toast.error("Enter a quantity greater than zero."); setBusy(false); return; }
        const r = await api("POST", `/products/${row.id}/receive`, { qty_received: recvQty, unit_cost: recvCost });
        toast.success(`Received ${recvQty} — new average cost ${money(r.cost_price)}.`);
        onDone({ ...row, qty: r.qty, cost_price: r.cost_price });
      }
      onClose();
    } catch (e) { toast.error(e.message || "Could not update item cost."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[130] bg-black/50 flex items-center justify-center p-4" data-testid="item-cost-modal" onClick={onClose}>
      <div className="w-full max-w-md bg-[#ece9d8] rounded-sm shadow-2xl border border-slate-500 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between bg-gradient-to-b from-[#2a6cb5] to-[#1a4f8a] px-3 py-2">
          <span className="text-white font-black text-sm truncate">Edit Item Cost — {row.name}</span>
          <button onClick={onClose} data-testid="item-cost-close" className="text-white/90 hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex gap-2">
            <button onClick={() => setMode("receive")} data-testid="item-cost-mode-receive" className={`flex-1 rounded-sm border px-2 py-1.5 text-[12px] font-black ${mode === "receive" ? "bg-[#1a4f8a] text-white border-[#123a63]" : "bg-white text-[#1a4f8a] border-[#9db8d6]"}`}>Receive Stock (recommended)</button>
            <button onClick={() => setMode("manual")} data-testid="item-cost-mode-manual" className={`flex-1 rounded-sm border px-2 py-1.5 text-[12px] font-black ${mode === "manual" ? "bg-[#1a4f8a] text-white border-[#123a63]" : "bg-white text-[#1a4f8a] border-[#9db8d6]"}`}>Manual Correction</button>
          </div>

          {mode === "receive" ? (
            <>
              <p className="text-[11px] text-slate-600 leading-snug">Enter how many units you received and the <b>GST-inclusive price you paid per unit</b>. The stock value uses <b>Weighted Average Cost</b> so old cheaper stock and new dearer stock blend into one correct average.</p>
              <div className="grid grid-cols-2 gap-2">
                <label className="text-[12px] font-bold text-slate-700">Qty Received
                  <input type="number" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="item-cost-qty" className={inp} /></label>
                <label className="text-[12px] font-bold text-slate-700">Unit Cost (inc GST)
                  <input type="number" step="0.01" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} data-testid="item-cost-unit" className={inp} /></label>
              </div>
              <div className="rounded-sm bg-white border border-[#9db8d6] p-2 text-[12px] text-slate-700 space-y-0.5">
                <div className="flex justify-between"><span>Current on hand</span><span className="font-mono2">{oldQty} @ {money(oldCost)}</span></div>
                <div className="flex justify-between"><span>Receiving</span><span className="font-mono2">{recvQty} @ {money(recvCost)}</span></div>
                <div className="flex justify-between font-black text-[#123a63] border-t border-slate-200 pt-0.5"><span>New on hand → avg cost</span><span className="font-mono2" data-testid="item-cost-projected">{projQty} @ {money(projCost)}</span></div>
              </div>
            </>
          ) : (
            <>
              <p className="text-[11px] text-slate-600 leading-snug">Use this only to fix a data entry error. It overwrites the cost without changing the quantity on hand.</p>
              <label className="text-[12px] font-bold text-slate-700 block">New Cost Price (inc GST)
                <input type="number" step="0.01" value={manualCost} onChange={(e) => setManualCost(e.target.value)} data-testid="item-cost-manual" className={inp} /></label>
            </>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="rounded-sm border border-slate-400 bg-white px-3 py-1.5 text-[12px] font-bold text-slate-600">Cancel</button>
            <button onClick={submit} disabled={busy} data-testid="item-cost-save" className="rounded-sm bg-[#1a4f8a] hover:bg-[#123a63] text-white px-4 py-1.5 text-[12px] font-black disabled:opacity-50 inline-flex items-center gap-1.5"><PackagePlus className="h-3.5 w-3.5" /> {mode === "manual" ? "Save Cost" : "Receive Into Stock"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Receiving / movement history for one part ----------
function HistoryModal({ pid, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api("GET", `/products/${pid}/movements`).then(setData).catch(() => setData({ product: null, movements: [] })); }, [pid]);
  const dt = (s) => { try { return new Date(s).toLocaleString("en-AU", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return s || ""; } };
  return (
    <div className="fixed inset-0 z-[130] bg-black/50 flex items-center justify-center p-4" data-testid="history-modal" onClick={onClose}>
      <div className="w-full max-w-3xl bg-white rounded-sm shadow-2xl border border-slate-500 overflow-hidden flex flex-col max-h-[88vh]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between bg-gradient-to-b from-[#2a6cb5] to-[#1a4f8a] px-3 py-2">
          <span className="text-white font-black text-sm inline-flex items-center gap-1.5"><History className="h-4 w-4" /> Movement History{data?.product ? ` — ${data.product.name}` : ""}</span>
          <button onClick={onClose} data-testid="history-close" className="text-white/90 hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-4 overflow-y-auto">
          {!data ? <p className="text-slate-500 text-sm py-6 text-center">Loading history…</p>
            : (
              <>
                {data.product && (
                  <div className="flex flex-wrap gap-x-6 gap-y-1 text-[12px] text-slate-700 mb-3">
                    <span>Current on hand: <b className="text-[#123a63]">{data.product.qty ?? 0}</b></span>
                    <span>Current avg cost (inc GST): <b className="text-[#123a63]">{money(data.product.cost_price)}</b></span>
                  </div>
                )}
                {data.movements.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-slate-500 text-[13px]" data-testid="history-empty">
                    No movement history yet. Deliveries (Edit Item Cost → Receive, or a Purchase Order) and stocktake adjustments (count / cost corrections) will appear here.
                  </div>
                ) : (
                  <table className="w-full border-collapse text-[12px]">
                    <thead><tr className="bg-[#dfe7f1] text-[#123a63] text-[11px] font-black">
                      <th className="px-2 py-1.5 text-left">Date</th><th className="px-2 py-1.5 text-right">Qty</th>
                      <th className="px-2 py-1.5 text-right">Unit Cost</th><th className="px-2 py-1.5 text-right">On Hand</th>
                      <th className="px-2 py-1.5 text-right">Avg Cost</th><th className="px-2 py-1.5 text-left">Reference</th><th className="px-2 py-1.5 text-left">By</th>
                    </tr></thead>
                    <tbody>
                      {data.movements.map((m) => { const adj = m.type === "adjustment"; return (
                        <tr key={m.id} data-testid="history-row" className="border-t border-slate-100">
                          <td className="px-2 py-1.5 whitespace-nowrap">{dt(m.at)}</td>
                          <td className={`px-2 py-1.5 text-right font-bold ${adj ? "text-slate-500" : "text-emerald-700"}`}>{adj ? (m.qty ? (m.qty > 0 ? `+${m.qty}` : m.qty) : "—") : `+${m.qty}`}</td>
                          <td className="px-2 py-1.5 text-right">{adj ? "—" : money(m.unit_cost)}</td>
                          <td className="px-2 py-1.5 text-right text-slate-500">{m.old_qty} → <b className="text-slate-800">{m.new_qty}</b></td>
                          <td className="px-2 py-1.5 text-right text-slate-500">{money(m.old_cost)} → <b className="text-[#123a63]">{money(m.new_cost)}</b></td>
                          <td className="px-2 py-1.5">{adj ? <span className="text-amber-700 font-semibold">{m.reason || "Adjustment"}</span> : (m.ref ? <span className="font-semibold text-[#123a63]">{m.ref}</span> : <span className="text-slate-400">Counter receipt</span>)}</td>
                          <td className="px-2 py-1.5 text-slate-600">{m.by || "—"}</td>
                        </tr>
                      ); })}
                    </tbody>
                  </table>
                )}
              </>
            )}
        </div>
      </div>
    </div>
  );
}

export default function Stocktake({ onExit, onNav, branding }) {
  const [f, setF] = useState({ q: "", brand: "", dealer: "", barcode: "" });
  const [lowOnly, setLowOnly] = useState(false);
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ value_inclusive: 0, gst: 0, value_exclusive: 0, units: 0, skus: 0 });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [opts, setOpts] = useState({ brands: [], dealers: [] });
  const [edits, setEdits] = useState({}); // id -> { qty, price, brand, supplier, itemCost, pricePer, qtyPer }
  const [costRow, setCostRow] = useState(null);
  const [histRow, setHistRow] = useState(null);
  const [reportMode, setReportMode] = useState("valuation");
  const [company, setCompany] = useState(null);

  useEffect(() => { api("GET", "/company-setup").then((r) => setCompany((r || [])[0] || null)).catch(() => {}); }, []);

  useEffect(() => { api("GET", "/stocktake/options").then((r) => setOpts({ brands: r?.brands || [], dealers: r?.dealers || [] })).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (f.q) qs.set("q", f.q);
    if (f.brand) qs.set("brand", f.brand);
    if (f.dealer) qs.set("dealer", f.dealer);
    if (f.barcode) qs.set("barcode", f.barcode);
    if (lowOnly) qs.set("low_only", "true");
    qs.set("limit", "300");
    try {
      const r = await api("GET", `/stocktake?${qs.toString()}`);
      setItems(r?.items || []); setTotals(r?.totals || {}); setTotal(r?.total || 0);
      setEdits({});
    } catch { setItems([]); setTotal(0); }
    finally { setLoading(false); }
  }, [f, lowOnly]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const setFilter = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const clearAll = () => setF({ q: "", brand: "", dealer: "", barcode: "" });

  const ed = (row) => {
    const d = {
      qty: String(row.qty ?? 0), price: String(row.price ?? 0), reorder: String(row.stock_alert ?? 0),
      brand: row.brand || "", supplier: row.supplier || "", itemCost: "", pricePer: "", qtyPer: "",
    };
    return { ...d, ...(edits[row.id] || {}) };
  };
  const setEd = (id, patch) => setEdits((x) => ({ ...x, [id]: { ...(x[id] || {}), ...patch } }));

  // Bulk calculator: Price Per / Qty Per -> Item Cost (unit cost you paid)
  const onBulk = (row, key, val) => {
    const base = ed(row);
    const next = { ...base, [key]: val };
    const pp = num(next.pricePer), qp = num(next.qtyPer);
    if (pp > 0 && qp > 0) next.itemCost = String(Math.round((pp / qp) * 100) / 100);
    setEd(row.id, next);
  };

  const saveRow = async (row) => {
    const e = ed(row);
    try {
      const r = await api("POST", `/products/${row.id}/stocktake`, {
        qty: parseInt(e.qty, 10) || 0, price: num(e.price), stock_alert: parseInt(e.reorder, 10) || 0,
        brand: e.brand || "", supplier: e.supplier || "",
      });
      setItems((list) => list.map((p) => (p.id === row.id ? { ...p, qty: r.qty, price: r.price, stock_alert: r.stock_alert, brand: r.brand, supplier: r.supplier } : p)));
      setEd(row.id, { itemCost: "", pricePer: "", qtyPer: "" });
      toast.success(`Updated ${row.name}.`);
    } catch (err) { toast.error(err.message || "Could not update this part."); }
  };

  const delRow = async (row) => {
    if (!window.confirm(`Delete "${row.name}" from stock? This cannot be undone.`)) return;
    try { await api("DELETE", `/products/${row.id}`); setItems((l) => l.filter((p) => p.id !== row.id)); toast.success("Part deleted."); }
    catch (err) { toast.error(err.message || "Could not delete this part."); }
  };

  const onCostDone = (updated) => setItems((list) => list.map((p) => (p.id === updated.id ? { ...p, qty: updated.qty ?? p.qty, cost_price: updated.cost_price } : p)));

  const valueOf = (row) => { const v = num(row.qty) * num(row.cost_price); return { inc: v, gst: v / 11, ex: v - v / 11 }; };

  // ---------- Reports (Stock valuation list / Stocktake count sheet) ----------
  const reportDefs = useMemo(() => ({
    valuation: {
      title: "Stock Valuation List",
      columns: [
        { label: "Description", key: "name" }, { label: "Brand", key: "brand" }, { label: "Dealer", key: "supplier" },
        { label: "Barcode", key: "barcode" }, { label: "Location", key: "location" }, { label: "Stock", key: "qty" },
        { label: "Cost inc GST", key: "cost_inc", money: true }, { label: "Value Inc", key: "value_inc", money: true },
        { label: "GST", key: "gst", money: true }, { label: "Value Ex", key: "value_ex", money: true },
      ],
    },
    count: {
      title: "Stocktake Count Sheet",
      columns: [
        { label: "Description", key: "name" }, { label: "Brand", key: "brand" }, { label: "Barcode", key: "barcode" },
        { label: "Location", key: "location" }, { label: "System Qty", key: "qty" }, { label: "Counted", key: "counted" },
      ],
    },
  }), []);

  const buildRows = async () => {
    // Pull the full filtered set for the report, not just the on-screen page.
    const qs = new URLSearchParams();
    if (f.q) qs.set("q", f.q); if (f.brand) qs.set("brand", f.brand);
    if (f.dealer) qs.set("dealer", f.dealer); if (f.barcode) qs.set("barcode", f.barcode);
    qs.set("limit", "5000");
    let list = items, tot = totals;
    try { const r = await api("GET", `/stocktake?${qs.toString()}`); list = r?.items || items; tot = r?.totals || totals; } catch { /* fall back to page */ }
    const rows = list.map((p) => {
      const v = num(p.qty) * num(p.cost_price);
      return { name: p.name, brand: p.brand || "", supplier: p.supplier || "", barcode: p.barcode || p.sku || "",
        location: p.location || "", qty: num(p.qty), cost_inc: num(p.cost_price), value_inc: v, gst: v / 11, value_ex: v - v / 11, counted: "" };
    });
    return { rows, tot };
  };

  const companyHead = () => {
    const c = company || {};
    const nm = c.name || branding?.company || "Glamour Pools and Spas";
    const addr = [c.address, [c.suburb, c.state, c.postcode].filter(Boolean).join(" ")].filter(Boolean).join(", ");
    const line2 = [c.phone ? `Ph ${c.phone}` : (branding?.phone || ""), c.email || branding?.email || "", c.website || ""].filter(Boolean).join(" &nbsp;·&nbsp; ");
    const abn = c.abn ? `ABN ${c.abn}${c.acn ? ` &nbsp;·&nbsp; ACN ${c.acn}` : ""}` : "";
    const lines = [addr, line2, abn].filter(Boolean).join("<br/>");
    return `<div style="border-bottom:2px solid #123a63;padding-bottom:8px;margin-bottom:12px">
      <div style="font-size:18px;font-weight:800;color:#123a63">${nm}</div>
      ${lines ? `<div style="font-size:11px;color:#475569;margin-top:3px;line-height:1.5">${lines}</div>` : ""}</div>`;
  };
  const companyName = () => (company?.name || branding?.company || "Glamour Pools and Spas");

  const renderReportHTML = (def, rows, tot, doPrint) => {
    const head = def.columns.map((col) => `<th style="text-align:${col.money ? "right" : "left"}">${col.label}</th>`).join("");
    const body = rows.map((r) => `<tr>${def.columns.map((col) => {
      const val = col.money ? money(r[col.key]) : (r[col.key] === "" || r[col.key] == null ? (col.key === "counted" ? "&nbsp;" : "") : String(r[col.key]));
      return `<td style="text-align:${col.money ? "right" : "left"}">${val}</td>`;
    }).join("")}</tr>`).join("");
    const totalsRow = reportMode === "valuation"
      ? `<tfoot><tr style="font-weight:800;background:#eef4fb">
          <td colspan="5" style="text-align:right">GRAND TOTAL (${tot.skus || rows.length} lines · ${tot.units || 0} units)</td>
          <td></td><td></td><td style="text-align:right">${money(tot.value_inclusive)}</td>
          <td style="text-align:right">${money(tot.gst)}</td><td style="text-align:right">${money(tot.value_exclusive)}</td></tr></tfoot>` : "";
    const w = window.open("", "_blank", "width=1100,height=900");
    if (!w) { toast.error("Please allow pop-ups to view the report."); return; }
    w.document.write(`<html><head><title>${def.title}</title><style>
      body{font-family:Arial,Helvetica,sans-serif;padding:22px;color:#111}
      h1{font-size:16px;margin:0 0 2px;color:#123a63}.sub{color:#64748b;font-size:12px;margin:0 0 14px}
      table{width:100%;border-collapse:collapse;font-size:11px}
      th,td{border:1px solid #cbd5e1;padding:4px 6px}th{background:#123a63;color:#fff}
      tr:nth-child(even) td{background:#f6f9fc}
      .ft{margin-top:14px;color:#94a3b8;font-size:10px;text-align:center}
    </style></head><body>${companyHead()}
      <h1>${def.title}</h1>
      <p class="sub">${new Date().toLocaleString("en-AU")}${f.brand ? ` · Brand: ${f.brand}` : ""}${f.dealer ? ` · Dealer: ${f.dealer}` : ""}${f.q ? ` · "${f.q}"` : ""}</p>
      <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody>${totalsRow}</table>
      <p class="ft">${companyName()}</p></body></html>`);
    w.document.close(); w.focus();
    if (doPrint) setTimeout(() => { try { w.print(); } catch { /* noop */ } }, 400);
  };

  const viewReport = async (doPrint) => {
    if (loading) return;
    const def = reportDefs[reportMode];
    const { rows, tot } = await buildRows();
    if (!rows.length) { toast.error("No stock lines match the current filter."); return; }
    renderReportHTML(def, rows, tot, doPrint);
  };

  const emailReport = async () => {
    const def = reportDefs[reportMode];
    const { rows } = await buildRows();
    if (!rows.length) { toast.error("No stock lines to email."); return; }
    exportXLSX(def.title, def.columns, rows);
    toast.success(`${def.title} downloaded — attach it to the email to your bookkeeper.`);
    const subject = encodeURIComponent(`${def.title} — ${branding?.company || "Aqualux Nexus"}`);
    const body = encodeURIComponent(`Hi,\n\nPlease find attached the ${def.title} as at ${new Date().toLocaleDateString("en-AU")}.\n\nThanks.`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <div data-testid="stocktake-panel" className="rounded-xl overflow-hidden bg-white border border-[#c3d0e6]">
      {/* Title bar */}
      <div className="flex items-center gap-2 bg-[#1a4f8a] px-3 py-1.5">
        <ClipboardList className="h-4 w-4 text-white" /><span className="text-white font-black text-sm">STOCKTAKE</span>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-end gap-x-4 gap-y-2 px-3 py-2 bg-[#eef2f7] border-b border-[#c3d0e6]">
        <button onClick={clearAll} data-testid="st-clear-all" className="rounded-sm bg-[#1a4f8a] hover:bg-[#123a63] text-white text-[12px] font-black px-3 py-1.5 inline-flex items-center gap-1.5"><RotateCcw className="h-3.5 w-3.5" /> Clear All</button>
        <label className="text-[11px] font-black text-[#123a63]">Barcode<input value={f.barcode} onChange={setFilter("barcode")} data-testid="st-f-barcode" placeholder="Scan / type…" className={`${inp} w-40`} /></label>
        <label className="text-[11px] font-black text-[#123a63]">Brand
          <select value={f.brand} onChange={setFilter("brand")} data-testid="st-f-brand" className={`${inp} w-40`}><option value="">All</option>{opts.brands.map((b) => <option key={b} value={b}>{b}</option>)}</select>
        </label>
        <label className="text-[11px] font-black text-[#123a63]">Part Description<input value={f.q} onChange={setFilter("q")} data-testid="st-f-desc" placeholder="Search description…" className={`${inp} w-52`} /></label>
        <label className="flex items-center gap-1.5 text-[11px] font-black text-[#123a63] cursor-pointer pb-1">
          <input type="checkbox" checked={lowOnly} onChange={(e) => setLowOnly(e.target.checked)} data-testid="st-low-only" className="h-3.5 w-3.5 accent-rose-600" /> Low stock only
        </label>
        {onNav && <button onClick={() => onNav("po:reorder")} data-testid="st-reorder-list" className="rounded-sm bg-amber-500 hover:bg-amber-600 text-white text-[12px] font-black px-3 py-1.5 inline-flex items-center gap-1.5"><PackagePlus className="h-3.5 w-3.5" /> Reorder List</button>}

        <div className="flex items-end gap-2 ml-auto">
          <label className="text-[11px] font-black text-[#123a63]">List
            <select value={reportMode} onChange={(e) => setReportMode(e.target.value)} data-testid="st-report-mode" className={`${inp} w-40`}>
              <option value="valuation">Stock Valuation</option>
              <option value="count">Stocktake Count Sheet</option>
            </select>
          </label>
          <button onClick={() => viewReport(false)} data-testid="st-view" className="rounded-sm bg-[#1a4f8a] hover:bg-[#123a63] text-white text-[12px] font-black px-3 py-1.5 inline-flex items-center gap-1.5"><Eye className="h-3.5 w-3.5" /> View</button>
          <button onClick={() => viewReport(true)} data-testid="st-print" className="rounded-sm bg-[#1a4f8a] hover:bg-[#123a63] text-white text-[12px] font-black px-3 py-1.5 inline-flex items-center gap-1.5"><Printer className="h-3.5 w-3.5" /> Print</button>
          <button onClick={emailReport} data-testid="st-email" className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-[12px] font-black px-3 py-1.5 inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Email</button>
          <button onClick={onExit} data-testid="st-close-form" className="rounded-sm border-2 border-[#123a63] bg-white text-[#123a63] text-[12px] font-black px-3 py-1.5 inline-flex items-center gap-1.5"><X className="h-3.5 w-3.5" /> Close This Form</button>
        </div>
      </div>

      {/* count + totals strip */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 px-3 py-1.5 bg-white border-b border-[#e2e8f0] text-[12px]">
        <span data-testid="st-result-count" className="text-slate-500">{loading ? "Loading stock…" : `Showing ${items.length} of ${total} parts`}</span>
        <span className="ml-auto text-slate-700">Total Value Inclusive: <b className="text-[#123a63]" data-testid="st-total-inc">{money(totals.value_inclusive)}</b></span>
        <span className="text-slate-700">GST: <b className="text-[#123a63]" data-testid="st-total-gst">{money(totals.gst)}</b></span>
        <span className="text-slate-700">Value Exclusive: <b className="text-[#123a63]" data-testid="st-total-ex">{money(totals.value_exclusive)}</b></span>
        <span className="text-slate-700">Units: <b className="text-[#123a63]">{totals.units}</b></span>
      </div>

      {/* Grid */}
      <div className="overflow-x-auto">
        <table className="min-w-[1580px] w-full border-collapse">
          <thead>
            <tr className="bg-[#dfe7f1] text-[#123a63] text-[11px] font-black">
              <th className="px-1.5 py-1 border border-[#b9b9b9] text-left w-[150px]">Brand</th>
              <th className="px-1.5 py-1 border border-[#b9b9b9] text-left w-[150px]">Dealer</th>
              <th className="px-1.5 py-1 border border-[#b9b9b9] text-left min-w-[220px]">Description</th>
              <th className="px-1.5 py-1 border border-[#b9b9b9] text-right w-[92px]">Retail Price</th>
              <th className="px-1.5 py-1 border border-[#b9b9b9] text-right w-[72px]">Price Per</th>
              <th className="px-1.5 py-1 border border-[#b9b9b9] text-right w-[64px]">Qty Per</th>
              <th className="px-1.5 py-1 border border-[#b9b9b9] text-right w-[80px]">Item Cost</th>
              <th className="px-1.5 py-1 border border-[#b9b9b9] w-[92px]"></th>
              <th className="px-1.5 py-1 border border-[#b9b9b9] text-right w-[96px]">Cost Price inc GST</th>
              <th className="px-1.5 py-1 border border-[#b9b9b9] text-right w-[74px]">Stock Level</th>
              <th className="px-1.5 py-1 border border-[#b9b9b9] text-right w-[70px]">Reorder At</th>
              <th className="px-1.5 py-1 border border-[#b9b9b9] w-[72px]"></th>
              <th className="px-1.5 py-1 border border-[#b9b9b9] text-right w-[92px]">Value Inclusive</th>
              <th className="px-1.5 py-1 border border-[#b9b9b9] text-right w-[80px]">GST</th>
              <th className="px-1.5 py-1 border border-[#b9b9b9] text-right w-[92px]">Value Exclusive</th>
              <th className="px-1.5 py-1 border border-[#b9b9b9] text-left w-[130px]">Barcode</th>
              <th className="px-1.5 py-1 border border-[#b9b9b9] w-[64px]"></th>
            </tr>
          </thead>
          <tbody>
            {!loading && items.length === 0 && (
              <tr><td colSpan={17} className="text-center text-slate-500 text-sm py-10">No parts match the current filter.</td></tr>
            )}
            {items.map((row) => {
              const e = ed(row); const v = valueOf(row);
              const low = num(row.qty) <= num(row.stock_alert);
              return (
                <tr key={row.id} data-testid="st-row" className={`align-middle ${low ? "bg-rose-50/60" : ""}`}>
                  <td className={cell}>
                    <select value={e.brand} onChange={(ev) => setEd(row.id, { brand: ev.target.value })} data-testid="st-brand" className={inp}>
                      <option value="">—</option>{[...new Set([e.brand, ...opts.brands].filter(Boolean))].map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </td>
                  <td className={cell}>
                    <select value={e.supplier} onChange={(ev) => setEd(row.id, { supplier: ev.target.value })} data-testid="st-dealer" className={inp}>
                      <option value="">—</option>{[...new Set([e.supplier, ...opts.dealers].filter(Boolean))].map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </td>
                  <td className="px-1.5 py-1 border border-[#b9b9b9] bg-[#c6f068] text-[12px] font-bold text-slate-900" data-testid="st-desc">{row.name}</td>
                  <td className={cell}><input type="number" step="0.01" value={e.price} onChange={(ev) => setEd(row.id, { price: ev.target.value })} data-testid="st-price" className={`${inp} text-right`} /></td>
                  <td className={cell}><input type="number" step="0.01" value={e.pricePer} onChange={(ev) => onBulk(row, "pricePer", ev.target.value)} data-testid="st-priceper" className={`${inp} text-right`} placeholder="0" /></td>
                  <td className={cell}><input type="number" value={e.qtyPer} onChange={(ev) => onBulk(row, "qtyPer", ev.target.value)} data-testid="st-qtyper" className={`${inp} text-right`} placeholder="0" /></td>
                  <td className={cell}><input type="number" step="0.01" value={e.itemCost} onChange={(ev) => setEd(row.id, { itemCost: ev.target.value })} data-testid="st-itemcost" className={yellowInp} placeholder="0.00" /></td>
                  <td className="px-1.5 py-1 border border-[#b9b9b9] text-center"><button onClick={() => setCostRow({ row, prefill: num(e.itemCost) || 0 })} data-testid="st-edit-cost" className={blueBtn}>Edit Item Cost</button></td>
                  <td className="px-1.5 py-1 border border-[#b9b9b9] text-right text-[12px] font-black text-[#123a63] bg-white" data-testid="st-costinc">{money(row.cost_price)}</td>
                  <td className={cell}><input type="number" value={e.qty} onChange={(ev) => setEd(row.id, { qty: ev.target.value })} data-testid="st-stocklevel" className={`${inp} text-right font-bold ${low ? "!bg-rose-100 !border-rose-400 text-rose-700" : ""}`} /></td>
                  <td className={cell}><input type="number" value={e.reorder} onChange={(ev) => setEd(row.id, { reorder: ev.target.value })} data-testid="st-reorder" title="Flag as low stock when Stock Level is at or below this" className={`${inp} text-right`} /></td>
                  <td className="px-1.5 py-1 border border-[#b9b9b9] text-center"><button onClick={() => saveRow(row)} data-testid="st-update" className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-black px-2.5 py-1">UPDATE</button></td>
                  <td className="px-1.5 py-1 border border-[#b9b9b9] text-right text-[12px] bg-white" data-testid="st-value-inc">{money(v.inc)}</td>
                  <td className="px-1.5 py-1 border border-[#b9b9b9] text-right text-[12px] bg-white" data-testid="st-value-gst">{money(v.gst)}</td>
                  <td className="px-1.5 py-1 border border-[#b9b9b9] text-right text-[12px] bg-white" data-testid="st-value-ex">{money(v.ex)}</td>
                  <td className="px-1.5 py-1 border border-[#b9b9b9] text-[11px] bg-white font-mono2" data-testid="st-barcode">{row.barcode || row.sku || ""}</td>
                  <td className="px-1.5 py-1 border border-[#b9b9b9] text-center whitespace-nowrap">
                    <button onClick={() => setHistRow(row)} data-testid="st-history" title="Receiving history" className="text-[#1a4f8a] hover:text-[#123a63] mr-1"><History className="h-4 w-4 inline" /></button>
                    <button onClick={() => delRow(row)} data-testid="st-delete" title="Delete part" className="text-rose-600 hover:text-rose-800"><Trash2 className="h-4 w-4 inline" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {total > items.length && !loading && (
        <p className="text-[11px] text-slate-400 py-2 text-center">Refine the Brand / Description / Barcode filters above to reach the remaining {total - items.length} parts.</p>
      )}

      {costRow && <ItemCostModal row={costRow.row} prefillCost={costRow.prefill} onClose={() => setCostRow(null)} onDone={onCostDone} />}
      {histRow && <HistoryModal pid={histRow.id} onClose={() => setHistRow(null)} />}
    </div>
  );
}
